import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { API, graphqlOperation } from 'aws-amplify'
import Button from 'antd/lib/button'
import message from 'antd/lib/message'
import Overlay from '../Overlay'
import { isTeamsToolEnabled } from '../../lib/helpers'
import { notifyTeamNotice, TEAM_NOTIFICATION } from '../../lib/teamNotifications'
import { isOwnMembership } from './useMyTeams'
import useListTeams, { adjustTeamMemberCountInCache } from '../Admin/Hooks/useListTeams'
import {
    useListAllTeamMembers,
    updateTeamMemberInCache,
    removeTeamMemberFromCache,
} from '../Admin/Hooks/useListTeamMembers'
import {
    patchTeamMemberInTeamCache,
    removeTeamMemberFromTeamCache,
} from '../Admin/Hooks/useGetTeam'
import { teamMembershipDeclined, teamMembershipAccepted } from '../../redux/app/actions'
import { entriesForPosition } from '../../lib/teamPrograms'
import { StorageService } from '../../lib/StorageService'

const GET_TEAM = `query GetTeam($pk: ID) {
    getTeam(pk: $pk) {
            pk
            name
            visibility
            active
            positions {
                title
                sk
                trainingPrograms {
                    pk
                    userType
                }
            }
    }
}`


// Backend stamps acceptedOn ("Accepted Team Assignment") with the accept date.
const ACCEPT_TEAM_ASSIGNMENT_MUTATION = `mutation UpdateTeamMember($pk: ID, $sk: String, $input: updateTeamMemberInput) {
    updateTeamMember(pk: $pk, sk: $sk, input: $input) {
        pk
        sk
        acceptedOn
    }
}`

// Backend removes the member and archives the membership with reason
// "Declined". The DECLINE notice to the configured recipients is sent from
// handleDecline via the team-notifications lambda.
const DECLINE_TEAM_ASSIGNMENT_MUTATION = `mutation DeleteTeamMember($pk: ID!, $sk: String!, $status: String) {
    deleteTeamMember(pk: $pk, sk: $sk, status: $status) {
        pk
        sk
        rolesRemoved
    }
}`

// Occupant-facing phrasing of the team's visibility setting — who can see the
// member's contact info once they join (VISIBILITY_OPTIONS in TeamConstants).
const VISIBILITY_NOTE = {
    ALL_BUILDING: 'the entire building',
    ALL_TENANT: 'your shared tenant',
    ALL_FLOOR: 'your shared floor',
    MEMBERS_ONLY: 'other team members',
}

// The decision key includes assignedOn so it identifies one ASSIGNMENT, not
// the membership slot: re-adding a user who declined writes a new record with
// a fresh assignedOn, which must prompt again immediately — while stale copies
// of the decided record (eventual consistency) share its assignedOn and stay
// suppressed.
const membershipKey = (m) => `${m.pk}|${m.sk}|${m.assignedOn ?? ''}`

// Decisions survive a page refresh for a few minutes: the backend's indexes
// are eventually consistent, so a quick refresh right after accepting or
// declining can re-serve the old record and would re-arm the popup otherwise.
// Short TTL on purpose — a genuine re-assignment to the same team later must
// still prompt.
const DECIDED_STORAGE_KEY = 'teamAssignmentDecisions'
const DECIDED_TTL_MS = 5 * 60 * 1000

const loadDecidedKeys = () => {
    const stored = StorageService.get(DECIDED_STORAGE_KEY) || {}
    const now = Date.now()
    const fresh = {}
    Object.entries(stored).forEach(([key, ts]) => {
        if (typeof ts === 'number' && now - ts < DECIDED_TTL_MS) fresh[key] = ts
    })
    StorageService.set(DECIDED_STORAGE_KEY, fresh)
    return new Set(Object.keys(fresh))
}

const rememberDecidedKey = (key) => {
    const stored = StorageService.get(DECIDED_STORAGE_KEY) || {}
    stored[key] = Date.now()
    StorageService.set(DECIDED_STORAGE_KEY, stored)
}

const describeGraphqlError = (e) =>
    e?.errors?.[0]?.message || (typeof e?.message === 'string' ? e.message : '')

const s = {
    content: {
        padding: '1.5rem 2rem 1.25rem 2rem',
        width: '520px',
        maxWidth: '92vw',
        color: '#333',
        textAlign: 'center',
    },
    intro: {
        fontSize: '14px',
        marginBottom: '16px',
    },
    fieldRow: {
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        fontSize: '14px',
        marginBottom: '6px',
    },
    // Mock: label column right-aligned so the colons line up, values in a
    // left-aligned column beside it, both at regular weight.
    fieldLabel: {
        width: '110px',
        textAlign: 'right',
        flexShrink: 0,
    },
    fieldValue: {
        width: '160px',
        textAlign: 'left',
    },
    confirmLine: {
        fontSize: '14px',
        margin: '16px 0 14px 0',
    },
    note: {
        fontSize: '11px',
        color: '#8c8c8c',
        marginBottom: '20px',
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
    },
}

// How long the popup stays hidden between two consecutive assignments, so the
// switch to the next team's popup reads as a new window rather than the
// previous one silently changing its text.
const NEXT_POPUP_DELAY_MS = 450

// The Accept/Decline popups are the LAST first-login windows: Confirm User
// Information shows first, then Terms of Use (when enabled), then these.
// hideForUserConfig / hideForTerms report those windows' pending-or-visible
// state from Guardian; the haveLoggedIn check below covers the async gap
// before Confirm User Information has decided to show.
const TeamAssignmentAcceptor = ({ onVisibilityChange, hideForUserConfig = false, hideForTerms = false }) => {
    const user = useSelector((state) => state.app.user)
    const building = useSelector((state) => state.app.building)
    const modules = building?.settings?.modules
    const teamMemberships = useSelector((state) => state.app.teamMember)

    const teamsD1 = building?.d1 && building?.pk ? `${building.d1}#${building.pk}` : null
    // Same gate as the /teams route, side menu, and dashboard card: the Teams
    // tool has its own 'teams' module key (a missing entry counts as on), so
    // the popup never appears on a building where Teams is switched off.
    const teamsEnabled = isTeamsToolEnabled(modules)

    // SET_USER_DATA never puts pk on the redux user — at login the identity
    // fields are email/d2 (pk only appears after a full-occupant dispatch,
    // e.g. saving the first-login confirmation). Gating on pk alone meant
    // returning users never got the accept/decline popup at all.
    const userReady = Boolean(user?.pk || user?.email || user?.d2)

    // Queue of { team, membership } still awaiting a decision — shown one at a
    // time. There is no way to dismiss the popup: the user must accept or
    // decline every pending assignment.
    const [pending, setPending] = useState([])
    const [submitting, setSubmitting] = useState(false)
    // Brief blank between two consecutive popups — see NEXT_POPUP_DELAY_MS.
    const [transitioning, setTransitioning] = useState(false)
    const transitionTimer = useRef(null)
    useEffect(() => () => clearTimeout(transitionTimer.current), [])

    // First-login users must finish Confirm User Information (and Terms, via
    // hideForTerms) before any accept/decline shows. UserConfigurator keys off
    // this same d1 + haveLoggedIn predicate and re-asserts the flag on save.
    // userD1 arrives with SET_USER_DATA; d1 only with useInit's later write —
    // reading either closes the gap where this popup could show first.
    const awaitingFirstLoginConfig = Boolean((user?.d1 || user?.userD1) && !user?.haveLoggedIn)
    const suppressed = hideForUserConfig || hideForTerms || awaitingFirstLoginConfig

    // Memberships from redux come from getOccupancy(d2) and miss records that
    // were written against one of the user's OTHER occupant associations (a
    // multi-association user assigned via a different occupant record). Scan
    // the building's teams as well — the same cached hooks useMyTeams uses —
    // so those memberships are found via the email fallback in isOwnMembership.
    const { teams, loading: teamsLoading } = useListTeams(teamsEnabled ? teamsD1 : null)
    const activeTeams = useMemo(() => (teams || []).filter((t) => t.active !== false), [teams])
    const { allTeamMembers, loading: membersLoading } = useListAllTeamMembers(activeTeams)

    // Tell the parent while any assignment is undecided (the transition gap
    // included) — and while the rosters are still loading, so the windows
    // that come after this one (Tenant Activation Policy, Estimated
    // Population) can't slip in before a pending assignment is known.
    const hasPending = pending.length > 0
    const rostersLoading = teamsEnabled && Boolean(teamsD1) && (teamsLoading || membersLoading)
    const blocking = hasPending || rostersLoading
    useEffect(() => {
        if (onVisibilityChange) onVisibilityChange(blocking)
    }, [blocking, onVisibilityChange])

    // Assignments already accepted or declined. Seeded from storage so the
    // guard survives a refresh (eventual consistency can re-serve a deleted
    // or just-updated record for a short while); without this a re-run of the
    // effect (e.g. after a decline) would re-queue a decided assignment.
    const decidedRef = useRef(null)
    if (decidedRef.current === null) decidedRef.current = loadDecidedKeys()

    const markDecided = useCallback((record) => {
        const key = membershipKey(record)
        decidedRef.current.add(key)
        rememberDecidedKey(key)
    }, [])

    useEffect(() => {
        let stale = false

        async function fetch() {
            const fromRedux = Array.isArray(teamMemberships)
                ? teamMemberships
                : (teamMemberships ? [teamMemberships] : [])

            const seen = new Set()
            const memberships = [...fromRedux, ...allTeamMembers].filter((m) => {
                if (!m || !isOwnMembership(m, user) || m.acceptedOn) return false
                const key = membershipKey(m)
                if (seen.has(key) || decidedRef.current.has(key)) return false
                seen.add(key)
                return true
            })

            if (memberships.length === 0) {
                // Nothing pending — clear any stale queue so a resolved
                // assignment can never keep the popup on screen.
                setPending((prev) => (prev.length ? [] : prev))
                return
            }

            // A multi-association user can end up with more than one membership
            // record on the same team (one per occupant record). That's a single
            // assignment: show one popup per team and let accept/decline act on
            // every record, otherwise the user is asked to decide twice.
            const byTeam = new Map()
            memberships.forEach((m) => {
                const list = byTeam.get(m.pk) || []
                list.push(m)
                byTeam.set(m.pk, list)
            })

            const positionId = (value) => (value ? `${value}`.split('#').pop() : null)

            const results = await Promise.all(
                [...byTeam.values()].map(async (records) => {
                    const membership = records[0]
                    if (records.length > 1) {
                        console.warn('Multiple membership records on one team for this user', records.map((r) => r.sk))
                    }
                    // One team failing to load (deleted, archived, or a partial
                    // GraphQL error) must not block the popups for the other
                    // pending assignments.
                    try {
                        const teamResponse = await API.graphql(
                            graphqlOperation(GET_TEAM, { pk: membership.pk })
                        )
                        const team = teamResponse.data.getTeam
                        if (!team || team.active === false) return null

                        const wanted = positionId(membership.positionId)
                        const position = wanted
                            ? (team.positions || []).find((p) => positionId(p.sk) === wanted) || null
                            : null

                        if (wanted && !position) {
                            console.warn('No matching position for membership', membership.sk, wanted)
                        }

                        return { team, membership: { ...membership, position }, records }
                    } catch (e) {
                        console.error('Error loading team for membership', membership.pk, e?.errors || e)
                        return null
                    }
                })
            )

            if (stale) return

            // Replace the queue wholesale (even with fewer or zero items) so a
            // stale entry — e.g. one restored by a fetch that raced a decision
            // — always gets corrected on the next pass. Re-check decisions
            // here too: the user may have accepted/declined while the team
            // lookups above were in flight (the membership list was filtered
            // before the click), and re-queuing that assignment forces them
            // to click Accept a second time.
            setPending(
                results
                    .filter(Boolean)
                    .filter((entry) =>
                        !(entry.records || [entry.membership]).some((r) => decidedRef.current.has(membershipKey(r)))
                    )
            )
        }

        if (userReady && teamsEnabled) fetch()
        return () => { stale = true }
    }, [teamMemberships, allTeamMembers, user, userReady, teamsEnabled])

    const advance = useCallback(() => {
        // More assignments waiting → hide the overlay for a beat first, so the
        // next one appears as its own popup instead of an in-place text swap.
        if (pending.length > 1) {
            setTransitioning(true)
            clearTimeout(transitionTimer.current)
            transitionTimer.current = setTimeout(() => setTransitioning(false), NEXT_POPUP_DELAY_MS)
        }
        setPending((prev) => prev.slice(1))
    }, [pending.length])

    const current = pending[0]

    const handleAccept = useCallback(async () => {
        if (submitting || !current) return
        setSubmitting(true)
        const records = current.records || [current.membership]
        let lastError = null
        try {
            const acceptedOn = Date.now()
            // Accept every membership record this user holds on the team (a
            // multi-association user may have one per occupant record) so the
            // assignment can't come back asking to be accepted again.
            const outcomes = await Promise.all(records.map((record) =>
                API.graphql(graphqlOperation(ACCEPT_TEAM_ASSIGNMENT_MUTATION, {
                    pk: record.pk,
                    sk: record.sk,
                    input: { acceptedOn },
                }))
                    .then((res) => {
                        // If the backend did not persist acceptedOn, the popup
                        // will come back at the next login no matter what the
                        // client does — make that failure visible.
                        if (!res?.data?.updateTeamMember?.acceptedOn) {
                            console.warn('updateTeamMember did not return acceptedOn — the backend may not be persisting it', record.sk, res?.data?.updateTeamMember)
                        }
                        return record
                    })
                    .catch((e) => {
                        console.error('Error accepting membership', record.sk, e?.errors || e)
                        lastError = e
                        return null
                    })
            ))
            const accepted = outcomes.filter(Boolean)
            if (accepted.length === 0) throw lastError || new Error('No membership record could be accepted')

            if (accepted.length < records.length) {
                const detail = describeGraphqlError(lastError)
                message.warning(
                    `Joined ${current.team.name}, but ${records.length - accepted.length} of your duplicate membership records could not be updated${detail ? ` (${detail})` : ''}. This assignment may prompt again.`,
                    6
                )
            } else {
                message.success(`You have joined ${current.team.name}.`)
            }
            // Grant the position's training-program roles locally so the
            // dashboard card, training portal, and training status show the
            // programs without requiring a fresh login, and stamp acceptedOn on
            // the redux memberships so this assignment is never re-queued.
            const rolesAdded = (current.membership.position?.trainingPrograms || [])
                .map((tp) => tp.userType)
                .filter(Boolean)
            const programsAdded = entriesForPosition(current.membership.position, current.team.pk)
            accepted.forEach((record, idx) => {
                teamMembershipAccepted({
                    pk: record.pk,
                    sk: record.sk,
                    acceptedOn,
                    rolesAdded: idx === 0 ? rolesAdded : [],
                    programsAdded: idx === 0 ? programsAdded : [],
                })
                markDecided(record)
                // Stamp the shared caches too — they outlive this component,
                // so a remount can't resurrect the assignment from a stale
                // cached copy, and any open team page updates live.
                updateTeamMemberInCache(record.pk, record.sk, { acceptedOn })
                patchTeamMemberInTeamCache(record.pk, record.sk, { acceptedOn })
            })
            // ACCEPT notice to the recipients configured in System
            // Notifications ("Send Notice When User Accepts Team Position").
            // Best-effort, after the accept has landed.
            notifyTeamNotice({
                type: TEAM_NOTIFICATION.ACCEPT,
                team: current.team,
                teamD1: teamsD1,
                buildingName: building?.name || '',
                actor: user,
                actorPosition: current.membership.position?.title || '',
            })
            advance()
        } catch (e) {
            console.error(e?.errors || e)
            const detail = describeGraphqlError(e)
            message.error(`Couldn't accept this assignment. ${detail ? `(${detail}) ` : ''}Try again.`)
        } finally {
            setSubmitting(false)
        }
    }, [submitting, current, advance, markDecided, teamsD1, building, user])

    const handleDecline = useCallback(async () => {
        if (submitting || !current) return
        setSubmitting(true)
        const records = current.records || [current.membership]
        let lastError = null
        try {
            // Decline every membership record this user holds on the team —
            // see handleAccept. The delete payload can come back null even when
            // the delete succeeded, so read it defensively: crashing here would
            // leave the popup stuck on a decided assignment.
            const outcomes = await Promise.all(records.map((record) =>
                API.graphql(graphqlOperation(DECLINE_TEAM_ASSIGNMENT_MUTATION, {
                    pk: record.pk,
                    sk: record.sk,
                    status: 'Has declined assignment'
                }))
                    .then((res) => ({ record, rolesRemoved: res?.data?.deleteTeamMember?.rolesRemoved || [] }))
                    .catch((e) => {
                        console.error('Error declining membership', record.sk, e?.errors || e)
                        lastError = e
                        return null
                    })
            ))
            const declined = outcomes.filter(Boolean)
            if (declined.length === 0) throw lastError || new Error('No membership record could be declined')

            if (declined.length < records.length) {
                // Partial: some duplicate records failed to delete server-side,
                // so the assignment will prompt again for those. A plain green
                // toast here would contradict the popup reappearing.
                const detail = describeGraphqlError(lastError)
                message.warning(
                    `Declined, but ${records.length - declined.length} of your duplicate membership records could not be removed${detail ? ` (${detail})` : ''}. This assignment may prompt again.`,
                    6
                )
            } else {
                message.success('You have declined this team assignment.')
            }
            declined.forEach(({ record, rolesRemoved }) => {
                teamMembershipDeclined({ pk: record.pk, sk: record.sk, rolesRemoved })
                markDecided(record)
                // Remove from the shared caches too — they outlive this
                // component, so a remount can't resurrect the assignment, any
                // open team page drops the row live, and the All Teams member
                // count stays in step.
                removeTeamMemberFromCache(record.pk, record.sk)
                removeTeamMemberFromTeamCache(record.pk, record.sk)
                adjustTeamMemberCountInCache(record.pk, -1)
            })
            // Sheet row 12: notify the recipients configured in System
            // Notifications. Best-effort, after the decline has landed.
            notifyTeamNotice({
                type: TEAM_NOTIFICATION.DECLINE,
                team: current.team,
                teamD1: teamsD1,
                buildingName: building?.name || '',
                actor: user,
                actorPosition: current.membership.position?.title || '',
            })
            advance()
        } catch (e) {
            console.error(e?.errors || e)
            const detail = describeGraphqlError(e)
            message.error(`Couldn't decline this assignment. ${detail ? `(${detail}) ` : ''}Try again.`)
        } finally {
            setSubmitting(false)
        }
    }, [submitting, current, advance, markDecided, teamsD1, building, user])

    if (!current || transitioning || suppressed) return null

    const visibilityNote = VISIBILITY_NOTE[current.team.visibility] || VISIBILITY_NOTE.ALL_BUILDING

    return (
        <Overlay
            headerText="Accept Team Assignment"
            visible={true}
            disableOutsideClick
            zIndex={3000}
        >
            <div style={s.content}>
                <div style={s.intro}>You have been assigned to the following team.</div>

                <div style={s.fieldRow}>
                    <span style={s.fieldLabel}>Team Name:</span>
                    <span style={s.fieldValue}>{current.team.name}</span>
                </div>
                <div style={s.fieldRow}>
                    <span style={s.fieldLabel}>Position Title:</span>
                    <span style={s.fieldValue}>{current.membership.position?.title || '—'}</span>
                </div>

                <div style={s.confirmLine}>Please confirm that you accept this position.</div>

                <div style={s.note}>
                    Note: As a member of this team, your contact info is visible to {visibilityNote}.
                </div>

                <div style={s.buttons}>
                    <Button disabled={submitting} onClick={handleDecline}>
                        Decline
                    </Button>
                    <Button type="primary" loading={submitting} onClick={handleAccept}>
                        Accept Position
                    </Button>
                </div>
            </div>
        </Overlay>
    )
}

export default TeamAssignmentAcceptor
